import { FirebaseApp, FirebaseOptions, getApp, getApps, initializeApp } from 'firebase/app';
import { Auth, browserLocalPersistence, connectAuthEmulator, initializeAuth } from 'firebase/auth';
import {
  addDoc,
  and,
  collection,
  collectionGroup,
  CollectionReference,
  connectFirestoreEmulator,
  doc,
  DocumentData,
  DocumentReference,
  DocumentSnapshot,
  FieldPath,
  Firestore,
  FirestoreError,
  getCountFromServer,
  getDoc,
  getDocs,
  initializeFirestore,
  limit,
  onSnapshot,
  orderBy,
  OrderByDirection,
  Query,
  query,
  QueryConstraint,
  QueryDocumentSnapshot,
  QueryFilterConstraint,
  QuerySnapshot,
  setDoc,
  SetOptions,
  SnapshotListenOptions,
  SnapshotOptions,
  startAfter,
  Timestamp,
  Unsubscribe,
  where,
} from 'firebase/firestore';
import { FirebaseStorage, getStorage, ref, uploadBytes } from 'firebase/storage';

import { env } from '@/env';

import { rollbar } from './rollbar';

export type Path = [path: string, ...pathSegments: string[]];
export type OrderParams = { field: string | FieldPath; order: OrderByDirection };

const firebaseConfig = env.NEXT_PUBLIC_FIREBASE_CONFIG as FirebaseOptions | undefined;

export let firebaseApp: FirebaseApp;
export let firestore: Firestore;
export let firebaseAuth: Auth;
export let firebaseStorage: FirebaseStorage;

try {
  if (firebaseConfig) {
    firebaseApp = !getApps().length ? initializeApp(firebaseConfig) : getApp();

    firebaseAuth = initializeAuth(firebaseApp, {
      persistence: browserLocalPersistence,
    });

    firestore = initializeFirestore(firebaseApp, {
      ignoreUndefinedProperties: true,
    });

    firebaseStorage = getStorage(firebaseApp);

    if (env.NEXT_PUBLIC_FIREBASE_AUTH_EMULATOR_HOST) {
      connectAuthEmulator(firebaseAuth, `http://${env.NEXT_PUBLIC_FIREBASE_AUTH_EMULATOR_HOST}`);
    }

    if (env.NEXT_PUBLIC_FIRESTORE_EMULATOR_HOST) {
      const [host, port] = env.NEXT_PUBLIC_FIRESTORE_EMULATOR_HOST.split(':');
      connectFirestoreEmulator(firestore, host, Number(port));
    }
  }
} catch (error) {
  rollbar.error('Error on firebase setup', error as Error);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  // Check if is an object and not an array or null
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function checkForTimestamp(data: unknown): unknown {
  if (data instanceof Timestamp) {
    // Check if is a timestamp and convert to date
    return data.toDate();
  }

  if (Array.isArray(data)) {
    // Check every item in the array
    return checkForTimestampInArray(data);
  }

  if (isRecord(data)) {
    // Check if is an object and check for timestamp prop
    return checkForTimestampProp(data);
  }

  return data;
}

function checkForTimestampInArray<TData>(data: Array<TData>): Array<unknown> {
  return data.map(checkForTimestamp);
}

function checkForTimestampProp(data: Record<string, unknown>): Record<string, unknown> {
  return Object.entries(data).reduce(
    (acc, [key, value]) => {
      acc[key] = checkForTimestamp(value);
      return acc;
    },
    {} as Record<string, unknown>,
  );
}

const converter = {
  toFirestore(data: DocumentData): DocumentData {
    return data;
  },
  fromFirestore(snapshot: QueryDocumentSnapshot, options: SnapshotOptions): DocumentData {
    const data = snapshot.data(options);
    return { ...checkForTimestampProp(data), id: snapshot.id };
  },
};

// Some firestore hooks need a DOCUMENT arg
export function getDocument<DocumentData>(path: string, ...pathSegments: string[]) {
  return doc(firestore, path, ...pathSegments).withConverter(
    converter,
  ) as DocumentReference<DocumentData>;
}

// Some firestore hooks need a COLLECTION arg
export function getCollection<DocumentData>(path: string, ...pathSegments: string[]) {
  return collection(firestore, path, ...pathSegments).withConverter(
    converter,
  ) as CollectionReference<DocumentData>;
}

// Some firestore hooks need a QUERY arg
export function getQuery<DocumentData>({
  path,
  order,
  query: queryParams = [],
  limit: limitValue,
  allowDeleted = false,
  startAfterDoc,
}: {
  path: Path;
  order: OrderParams[];
  limit?: number;
  query?: [...queryConstraints: QueryConstraint[]];
  allowDeleted?: boolean;
  startAfterDoc?: QueryDocumentSnapshot<DocumentData>;
}) {
  const ref = getCollection(...path);

  // Build query constraints array safely
  const constraints: QueryConstraint[] = [];

  // Add soft delete filter
  if (!allowDeleted) {
    constraints.push(where('deletedAt', '==', null));
  }

  // Add custom query params
  if (queryParams && Array.isArray(queryParams)) {
    constraints.push(...queryParams);
  }

  // Add limit
  if (limitValue && typeof limitValue === 'number' && limitValue > 0) {
    constraints.push(limit(limitValue));
  }

  // Add ordering - validate each order param
  order.forEach((orderParam) => {
    constraints.push(orderBy(orderParam.field, orderParam.order));
  });

  if (startAfterDoc) {
    constraints.push(startAfter(startAfterDoc));
  }

  return query(ref, ...constraints) as Query<DocumentData>;
}

// To use with and | or
export function getCompoundQuery<DocumentData>({
  path,
  order,
  query: queryParams = [],
  limit: limitValue,
}: {
  path: Path;
  order: [OrderParams, ...OrderParams[]];
  limit?: number;
  query?: [...queryConstraints: QueryFilterConstraint[]];
}) {
  const ref = getCollection(...path);

  return query(
    ref,
    and(
      where('deletedAt', '==', null), // TODO: remove this if we're not using soft delete
      ...(queryParams ? queryParams : []),
    ),
    ...(limitValue ? [limit(limitValue)] : []),
    ...order.map(({ field, order }) => orderBy(field, order)),
  ) as Query<DocumentData>;
}

export function getCollectionGroupQuery<DocumentData>({
  collectionName,
  order,
  query: queryParams = [],
  limit: limitValue,
}: {
  collectionName: string;
  order: [OrderParams, ...OrderParams[]];
  limit?: number;
  query?: [...queryConstraints: QueryConstraint[]];
}) {
  const ref = collectionGroup(firestore, collectionName);

  return query(
    ref,
    where('deletedAt', '==', null), // TODO: remove this if we're not using soft delete
    ...(queryParams ? queryParams : []),
    ...(limitValue ? [limit(limitValue)] : []),
    ...order.map(({ field, order }) => orderBy(field, order)),
  ) as Query<DocumentData>;
}

/**
 * Create new document on firestore
 * @param path
 * @param data
 * @param user
 */
export async function createData<T>(path: Path, data: T) {
  const collection = getCollection<T>(...path);
  return addDoc(collection, { ...data, createdAt: Timestamp.now(), deletedAt: null });
}

/**
 * Update document on firestore
 * @param path
 * @param id
 * @param data
 * @param options
 */
export async function updateData<T>(
  path: Path,
  id: string,
  data: Partial<T>,
  options?: SetOptions,
) {
  const item = getDocument<T>(...path, id);
  return setDoc(item, { ...data, updatedAt: Timestamp.now() }, options || { merge: true });
}

/**
 * Delete document on firestore
 * @param path
 * @param id
 * @param data
 * @param options
 */
export async function softDelete(path: Path, id: string) {
  return updateData(path, id, { deletedAt: Timestamp.now() }, { merge: true });
}

/**
 * Get collection data from firestore
 * @param query
 */
export async function getCollectionData<T>(query: Query<T>) {
  const { docs } = await getDocs<T, DocumentData>(query);
  const data = docs.map((d) => ({ ...(d.data() as T), id: d.id }) as T);
  return data;
}

/**
 * Get document data from firestore
 * @param query
 */
export async function getDocumentData<T>(path: Path) {
  const doc = await getDoc<T, DocumentData>(getDocument(...path));

  return { ...(doc.data() as T), id: doc.id } as T;
}

function isQuerySnapshot<T extends DocumentData>(
  snapshot: QuerySnapshot<T> | DocumentSnapshot<T>,
): snapshot is QuerySnapshot<T> {
  return (snapshot as QuerySnapshot<T>).docs !== undefined;
}

export function onDataSnapshot<T extends DocumentData>(
  reference: DocumentReference<T>,
  onNext: (data: T, snapshot: DocumentSnapshot<T>) => void,
  onError?: (error: FirestoreError) => void,
  options?: SnapshotListenOptions,
  onCompletion?: () => void,
): Unsubscribe;

export function onDataSnapshot<T extends DocumentData>(
  query: Query<T>,
  onNext: (data: T[], snapshot: QuerySnapshot<T>) => void,
  onError?: (error: FirestoreError) => void,
  options?: SnapshotListenOptions,
  onCompletion?: () => void,
): Unsubscribe;

export function onDataSnapshot<T extends DocumentData>(
  query: Query<T> | DocumentReference<T>,
  onNext:
    | ((data: T[], snapshot: QuerySnapshot<T>) => void)
    | ((data: T, snapshot: DocumentSnapshot<T>) => void),
  onError?: (error: FirestoreError) => void,
  options?: SnapshotListenOptions,
  onCompletion?: () => void,
): Unsubscribe {
  const handleNext = (snapshot: QuerySnapshot<T> | DocumentSnapshot<T>) => {
    if (isQuerySnapshot(snapshot)) {
      const next = onNext as (data: T[], snapshot: QuerySnapshot<T>) => void;
      const data = snapshot.empty
        ? []
        : snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
      next(data, snapshot);
    } else {
      const next = onNext as (data: T, snapshot: DocumentSnapshot<T>) => void;
      // TODO: review not found documents
      if (!snapshot.exists()) {
        next(null as unknown as T, snapshot);
      } else {
        const data = { ...snapshot.data(), id: snapshot.id } as T;
        next(data, snapshot);
      }
    }
  };

  if (options) {
    return onSnapshot(query as Query<T>, options, handleNext, onError, onCompletion);
  } else {
    return onSnapshot(query as Query<T>, handleNext, onError, onCompletion);
  }
}

export async function getCount(path: Path, queryParams: QueryFilterConstraint[]): Promise<number> {
  const ref = getCollection(...path);
  const queryResult = query(ref, and(where('deletedAt', '==', null), ...queryParams));
  const snapshot = await getCountFromServer(queryResult);
  return snapshot.data().count;
}

export async function uploadFileToStorage(storagePath: string, file: File): Promise<void> {
  const storageRef = ref(firebaseStorage, storagePath);
  await uploadBytes(storageRef, file);
}
